import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  Zap,
  Users,
  Briefcase,
  Mail,
  Heart,
  Globe,
  Shield,
  Target,
  Rocket,
  TrendingUp,
  Star,
  CheckCircle2,
} from 'lucide-react';
import landingService from '../../../services/landingService';
import LandingNavbar from '../LandingNavbar';
import LandingFooter from '../LandingFooter';
import './CompanyPage.css';

const ICON_MAP = { Users, Target, Globe, Shield, Heart, Briefcase, Mail, Star, Rocket, TrendingUp, Zap };

const companySections = [
  {
    id: 'about',
    icon: Heart,
    title: 'About Us',
    subtitle: 'Our mission and team',
    description:
      'Learn about our journey, our values, and the passionate team behind ResearchPro. We\'re on a mission to make research collaboration effortless, discovery instant, and impact measurable.',
    to: '/about',
    color: '#00C853',
    highlights: ['Our Mission', 'Core Values', 'Leadership Team', 'Company Milestones'],
    stat: { value: '50K+', label: 'Researchers' },
  },
  {
    id: 'careers',
    icon: Briefcase,
    title: 'Careers',
    subtitle: 'Join our growing team',
    description:
      'We\'re a remote-first team building the future of academic collaboration. Explore open positions across engineering, design, research, and more.',
    to: '/careers',
    color: '#7C4DFF',
    highlights: ['Remote-First Culture', 'Competitive Benefits', 'Open Positions', 'Growth Opportunities'],
    stat: { value: '12+', label: 'Open Roles' },
  },
  {
    id: 'contact',
    icon: Mail,
    title: 'Contact',
    subtitle: 'Get in touch with us',
    description:
      'Have a question, partnership idea, or feedback? Reach out to our team and we\'ll get back to you as soon as possible. We\'d love to hear from you.',
    to: '/contact',
    color: '#2979FF',
    highlights: ['General Inquiries', 'Partnership Opportunities', 'Press & Media', 'Technical Support'],
    stat: { value: '< 24h', label: 'Response Time' },
  },
];

const CompanyPage = () => {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [companyStats, setCompanyStats] = useState([]);
  const sectionRefs = useRef({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Fetch stats from API
  useEffect(() => {
    const fetchStats = async () => {
      const result = await landingService.getPublicStats();
      if (result.success && result.stats) {
        const s = result.stats;
        setCompanyStats([
          { value: typeof s.totalResearchers === 'number' ? s.totalResearchers.toLocaleString() + '+' : s.totalResearchers, label: 'Researchers worldwide', icon: 'Users' },
          { value: s.totalPapers || '200M+', label: 'Papers indexed', icon: 'Target' },
          { value: (s.totalCountries || 180) + '+', label: 'Countries represented', icon: 'Globe' },
          { value: s.platformUptime || '99.9%', label: 'Platform uptime', icon: 'Shield' },
        ]);
      }
    };
    fetchStats();
  }, []);

  const isVisible = (id) => visibleSections.has(id);
  const resolveIcon = (name) => ICON_MAP[name] || Users;

  return (
    <div className="cmp-page">
      <LandingNavbar />

      {/* ── Hero ── */}
      <section
        id="cmp-hero"
        className={`cmp-hero cmp-animate ${isVisible('cmp-hero') ? 'cmp-visible' : ''}`}
        ref={(el) => (sectionRefs.current['cmp-hero'] = el)}
      >
        <div className="cmp-hero-inner">
          <h1 className="cmp-hero-title">
            Building the future of<br />
            <span className="cmp-hero-gradient">research collaboration</span>
          </h1>
          <p className="cmp-hero-subtitle">
            We're a passionate team on a mission to make academic collaboration
            seamless. Learn about who we are, explore career opportunities, or get
            in touch.
          </p>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section
        id="cmp-stats"
        className={`cmp-stats-section cmp-animate ${isVisible('cmp-stats') ? 'cmp-visible' : ''}`}
        ref={(el) => (sectionRefs.current['cmp-stats'] = el)}
      >
        <div className="cmp-stats-inner">
          {companyStats.map((stat, i) => {
            const Icon = resolveIcon(stat.icon);
            return (
              <div key={stat._id || i} className="cmp-stat" style={{ '--stat-delay': `${i * 100}ms` }}>
                <div className="cmp-stat-icon">
                  <Icon size={20} />
                </div>
                <span className="cmp-stat-value">{stat.value}</span>
                <span className="cmp-stat-label">{stat.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Section Cards ── */}
      <section
        id="cmp-cards"
        className={`cmp-cards-section cmp-animate ${isVisible('cmp-cards') ? 'cmp-visible' : ''}`}
        ref={(el) => (sectionRefs.current['cmp-cards'] = el)}
      >
        <div className="cmp-cards-grid">
          {companySections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Link
                key={section.id}
                to={section.to}
                className="cmp-card"
                style={{ '--card-color': section.color, '--card-delay': `${index * 100}ms` }}
              >
                <div className="cmp-card-header">
                  <div className="cmp-card-icon">
                    <Icon size={22} />
                  </div>
                  <div className="cmp-card-stat">
                    <span className="cmp-card-stat-value">{section.stat.value}</span>
                    <span className="cmp-card-stat-label">{section.stat.label}</span>
                  </div>
                </div>

                <h2 className="cmp-card-title">{section.title}</h2>
                <p className="cmp-card-subtitle">{section.subtitle}</p>
                <p className="cmp-card-desc">{section.description}</p>

                <div className="cmp-card-highlights">
                  {section.highlights.map((h, i) => (
                    <span key={i} className="cmp-card-highlight">
                      <CheckCircle2 size={12} />
                      {h}
                    </span>
                  ))}
                </div>

                <span className="cmp-card-link">
                  Explore {section.title}
                  <ArrowRight size={15} />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Values Section ── */}
      <section
        id="cmp-values"
        className={`cmp-values-section cmp-animate ${isVisible('cmp-values') ? 'cmp-visible' : ''}`}
        ref={(el) => (sectionRefs.current['cmp-values'] = el)}
      >
        <div className="cmp-values-inner">
          <div className="cmp-values-header">
            <h2 className="cmp-values-title">What drives us</h2>
            <p className="cmp-values-subtitle">
              Our core values shape everything we build, from platform features to team culture.
            </p>
          </div>
          <div className="cmp-values-grid">
            {[
              { icon: Rocket, title: 'Move Fast', desc: 'Ship meaningful improvements every week. Speed with purpose.' },
              { icon: Globe, title: 'Open by Default', desc: 'Research belongs to everyone. We break silos and democratize access.' },
              { icon: Shield, title: 'Privacy First', desc: 'End-to-end encryption and zero-knowledge architecture. Your data is yours.' },
              { icon: Star, title: 'Researcher-Led', desc: 'Every feature starts with researcher feedback. Our users shape our roadmap.' },
              { icon: TrendingUp, title: 'Impact Driven', desc: 'We measure success by research outcomes, not vanity metrics.' },
              { icon: Heart, title: 'Community First', desc: 'We invest deeply in the communities that make research thrive.' },
            ].map((val, i) => {
              const VIcon = val.icon;
              return (
                <div key={i} className="cmp-value-card" style={{ '--val-delay': `${i * 80}ms` }}>
                  <div className="cmp-value-icon">
                    <VIcon size={20} />
                  </div>
                  <h3 className="cmp-value-title">{val.title}</h3>
                  <p className="cmp-value-desc">{val.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section
        id="cmp-cta"
        className={`cmp-cta cmp-animate ${isVisible('cmp-cta') ? 'cmp-visible' : ''}`}
        ref={(el) => (sectionRefs.current['cmp-cta'] = el)}
      >
        <div className="cmp-cta-inner">
          <span className="cmp-cta-badge">
            <Zap size={13} />
            Join Us
          </span>
          <h2 className="cmp-cta-title">Ready to shape the future of research?</h2>
          <p className="cmp-cta-subtitle">
            Whether you want to join the team, partner with us, or simply try the platform — we'd love to connect.
          </p>
          <div className="cmp-cta-actions">
            <Link to="/careers" className="cmp-btn cmp-btn--primary">
              View Open Positions
              <ArrowRight size={18} />
            </Link>
            <Link to="/signup" className="cmp-btn cmp-btn--secondary">
              Create Free Account
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default CompanyPage;
